'use client'

import React, { useMemo } from 'react'
import { ErrorMessage } from '@hookform/error-message'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { Controller, FieldValues, RegisterOptions } from 'react-hook-form'
import Select from 'react-select'

import { ButtonV2 } from '@fsg/gui-bits'
import { ChevronDown, Trash2, Plus } from '@fsg/icons'

import { cn } from '@app/utils'

import { PreferredIndexButton } from '@app/components/Form/PreferredIndexButton'
import { PhoneButton } from '@app/components/PhoneButton'
import { VALIDATIONS } from '@app/constants'
import { AccountResponse, ActivityResponse, ContactResponse, CountryResponseType, LeadResponse, OpportunityResponse, SiteResponse } from '@app/types'
import { formatCallingCodeOption, getSelectStyles, renderErrorMessage } from '@app/utils'
import layoutStyles from './Form.module.scss'
import { useFormContext } from './Form'
import { FormItem } from './FormItem'
import { PhoneInputAdapter } from '../components/ui/phone-input-adapter'

const classes = {
  base: 'text-sm font-regular text-true-black',
  editMode: 'bg-white border rounded-xs border-gray py-xs px-[12px]',
  wrapper: 'flex items-center'
}

const displayClassName = cn(classes.base)
const getInputClassName = (isEditing: boolean) => cn(classes.base, { [classes.editMode]: isEditing })

interface Props {
  name: string
  phoneInputProps?: PhoneInputProps
  removePhone: (index: number) => void
  updateState: React.Dispatch<React.SetStateAction<ActivityResponse[]>>
  phoneIndex: number
  entity: AccountResponse | LeadResponse | OpportunityResponse | ContactResponse | SiteResponse
  opportunity?: OpportunityResponse
  group?: string
  disablePrimary?: boolean
  placeholder?: string
  numberRequired?: number
  countries: CountryResponseType
}

export function PhoneNumberField(props: Props) {
  const {
    isEditing,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext()

  const {
    entity,
    updateState,
    opportunity,
    placeholder = "No phone number provided",
    disablePrimary,
    removePhone,
    phoneIndex,
    phoneInputProps,
    countries,
    // numberRequired,
  } = props

  const phoneNumberValue = watch(`${props.name}.number`);
  const phoneLabel = watch(`${props.name}.label`) || "Phone";

  const currentCallingCode: ReturnType<typeof formatCallingCodeOption> = watch(`${props.name}.callingCode`)
  const currentPhoneNumber: string = watch(`${props.name}.number`)
  const currentExtension: string = watch(`${props.name}.extension`)
  

  const [showExtensionWarning, setShowExtensionWarning] = React.useState(false);
  
  React.useEffect(() => {    
    if (currentExtension && (!currentPhoneNumber || currentPhoneNumber.trim() === '')) {  
      setShowExtensionWarning(true);
    } else {
      setShowExtensionWarning(false);
    }
  }, [currentExtension, currentPhoneNumber])
  
  function handleFocus() {
    clearErrors(`${props.group}.arrayError`)
  }

  const onClickRemove = () => removePhone(phoneIndex);

  const icon = (
    <PhoneButton
      updateState={updateState}
      opportunity={opportunity ?? null}
      entityId={entity.id}
      entity={entity}
      className="float-right"
      iconClass="bg-transparent w-[14px] h-[14px]"
      phoneNumber={`+${currentCallingCode?.value.telephoneCode}${phoneNumberValue}`}
    />
  )

  return (
    <div className="rounded-md bg-white">
      {/* Header row with label and action buttons */}
      <div className="flex justify-between w-full mb-2">
        <div className="flex items-center">
          <span className="flex items-center gap-1 text-sm font-medium">
            {props.numberRequired > 0 && isEditing
              ? VALIDATIONS.styleLabelString(phoneLabel)
              : phoneLabel}
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </span>
        </div>
        <div className="flex items-center gap-[6px]">
          <PreferredIndexButton
            group={props.group}
            index={phoneIndex}
            fieldName="setPrimaryPhone"
          />
          <RemoveButton onClick={onClickRemove} />
          {!isEditing && icon}
        </div>
      </div>

      {/* Phone input fields */}
      {isEditing ? (
        <div className="w-full">
          <div className="flex w-full gap-2">
            <div className="flex-1">
              <PhoneInputAdapter
                name={`${props.name}.number`}
                handleFocus={handleFocus}
                rules={{
                  required: props.numberRequired ? true : false,
                }}
                placeholder="Phone number"
                className="w-full"
              />
            </div>
            <div style={{ width: '55px' }}>
              <ExtensionInput
                name={`${props.name}.extension`}
                handleFocus={handleFocus}
              />
            </div>
          </div>
          <ErrorMessage errors={errors} name={`${props.name}.number`} render={renderErrorMessage} />
          {showExtensionWarning && (
            <div>{renderErrorMessage({ message: 'You must provide a phone number when adding an extension' })}</div>
          )}
        </div>
      ) : (
        <Display phoneNumber={currentPhoneNumber} callingCode={currentCallingCode} extension={currentExtension} placeholder={placeholder} />
      )}
    </div>
  );
}

type CallingCodeSelectProps = {
  name: string
  handleFocus: () => void
  countries: CountryResponseType
}
function CallingCodeSelect(props: CallingCodeSelectProps) {
  const { control } = useFormContext()
  const { name, handleFocus, countries } = props

  const callingCodeOptionsMap = useMemo(() => {
    return new Map(countries.map((country) => [country.alpha2code, formatCallingCodeOption(country)]))
  }, [countries])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="flex items-center">
          <Select
            onFocus={() => handleFocus()}
            options={Array.from(callingCodeOptionsMap.values())}
            isClearable={false}
            {...field}
            {...getSelectStyles()}
            styles={{
              control: (provided) => ({
                ...provided,
                border: 'none',
                boxShadow: 'none',
                minHeight: '28px',
                height: '28px'
              }),
              valueContainer: (provided) => ({
                ...provided,
                padding: '0',
                height: '28px'
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                height: '28px'
              })
            }}
          />
        </div>
      )}
    />
  );
}

type RemoveButtonProps = {
  onClick: () => void;
};

function RemoveButton({ onClick }: RemoveButtonProps) {
  const { isEditing } = useFormContext();

  return isEditing ? (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center text-red-600 hover:bg-red-50 rounded-2xs p-0"
    >
      <Trash2 className="h-[14px] w-[16px]" />
    </button>
  ) : null;
}

type DisplayProps = {
  phoneNumber: string;
  callingCode: ReturnType<typeof formatCallingCodeOption>;
  extension: string;
  placeholder: string;
};

function Display(props: DisplayProps) {
  const { phoneNumber, callingCode, extension, placeholder } = props;
  try {
    let content;   
    if (!phoneNumber) {
      content = placeholder;
    }

    const phone = parsePhoneNumber(
      phoneNumber,
      callingCode.value.alpha2code as CountryCode
    );
    
    if (!phone) throw new Error("Error parsing number");

  
    const _extension = extension ? `ext.${extension}` : null;

    content = (
      <>
        +{callingCode.value.telephoneCode} {phone.format("NATIONAL")}{" "}
        <span className="text-gray-700">{_extension}</span>
      </>
    );

    return <span className={displayClassName}>{content}</span>;
  } catch (e) {
    return <span className="text-error">Error</span>;
  }
}

interface ExtensionInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  handleFocus: () => void;
}

function ExtensionInput({
  name,
  handleFocus,
  ...inputProps
}: ExtensionInputProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div
          className="flex items-center justify-center max-w-[75px] h-9 px-xs py-xs pl-sm gap-4xs flex-1 rounded-xs border border-gray bg-white"
        >
          <input
            {...field}
            {...inputProps}
            onFocus={() => handleFocus()}
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              input.value = input.value.replace(/\D/g, '');
              field.onChange(input.value);
            }}
            className="w-full border-none bg-transparent p-0 text-sm focus:outline-none"
            placeholder="ext"
            type="tel"
            maxLength={6}
          />      
        </div>
      )}
    />
  );
}

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  rules: Omit<RegisterOptions<FieldValues, `${string}.number`>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>
  handleFocus: () => void
}

function PhoneInput({ name, rules, handleFocus, placeholder, ...inputProps }: PhoneInputProps) {
  const { control, isEditing } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        pattern: VALIDATIONS.PHONE_PATTERN,
        validate: (value: any) => {
          if (!value || !value.trim()) {
            return 'Number is required'
          }
          return true
        },
      }}
      render={({ field }) => (
        <input
          {...field}
          {...inputProps}
          onFocus={() => handleFocus()}
          className={getInputClassName(isEditing)}
          placeholder="Phone number"
          type="tel"
          style={{ padding: '8px' }}
        />
      )}
    />
  );
}
